import { runtimeConfig } from "@/runtime-config";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { getEnvAndRegionFromString } from "@/utils/env-utils";
import {
  isInternalUserEmail,
  PrivateData,
  useAnalyticsInitialization,
} from "@faro-lotv/foreign-observers";
import { assert, isLocalhost } from "@faro-lotv/foundation";
import { selectCompanyId } from "@faro-lotv/project-source";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

/**
 * Initialize the Amplitude client with all the properties of the Viewer app
 *
 * @param privateData A map with the private data that should be censored.
 */
export function useInitAppAnalytics(privateData?: PrivateData): void {
  const { projectId } = useParams();
  assert(projectId, "No project ID provided");

  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);

  const [env, region] = getEnvAndRegionFromString(
    runtimeConfig.deployment.environment,
  );
  const {
    viewerDevApiKey,
    viewerProdApiKey,
    externalsApiKey,
    internalsApiKey,
  } = runtimeConfig.analytics;

  // Collect the list of Amplitude projects we want to send the events during the session
  const keys = useMemo(() => {
    const isProd = env === "prod";
    const isInternal = isInternalUserEmail(currentUser?.email);

    if (isInternal) {
      return [
        ...(viewerDevApiKey ? [viewerDevApiKey] : []),
        ...(internalsApiKey ? [internalsApiKey] : []),
      ];
    }
    if (isProd) {
      return [
        ...(viewerProdApiKey ? [viewerProdApiKey] : []),
        ...(externalsApiKey ? [externalsApiKey] : []),
      ];
    }
    return viewerDevApiKey ? [viewerDevApiKey] : [];
  }, [
    currentUser?.email,
    env,
    externalsApiKey,
    internalsApiKey,
    viewerDevApiKey,
    viewerProdApiKey,
  ]);

  // Only the new Sphere Platform Amplitude projects have Guides & Surveys enabled
  const shouldEnableSurveys = useCallback(
    (key: string): boolean =>
      key === internalsApiKey || key === externalsApiKey,
    [externalsApiKey, internalsApiKey],
  );

  useAnalyticsInitialization({
    app: "Viewer",
    keys,
    currentUserEmail: currentUser?.email,
    companyId,
    projectId,
    userId: currentUser?.id,
    region,
    environment: env === "prod" ? "production" : env,
    privateData,
    shouldEnableSurveys,
    ignoreInternalUsers: isLocalhost(location.hostname),
    defaultTracking: false,
  });
}
